import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { getUserPersonalInfo } from '../../endpoints/usersTs';
import { getCountries, getStates } from '../../endpoints/lookupsTs';
import { useApiGet } from '../../utils/httpClientTs';
import { useApiSend, getApiErrorMessage } from '../../utils/httpClientTs';
import {
  type ChapterSuppliesCartItem,
  getCart,
  getCartSubtotal,
  setCartLineCustomText,
  setCartLinePersonalization,
  storefrontTextCharCount,
} from '../../utils/chapterSuppliesCart';
import {
  checkoutStorefront,
  confirmStorefrontPayment,
} from '../../endpoints/chapterSupplies';
import type { StorefrontCheckoutResponse } from '../../types/chapterSupplies';
import { config } from '../../utils/config';
import PersonalizationMemberPicker from './PersonalizationMemberPicker';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const clipToMaxChars = (val: string, max: number): string =>
  [...val].slice(0, max).join('');
const stripePublishableKey = (config.STRIPE_PUBLISHABLE_KEY || '').trim();
const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

const getFreight = (subtotal: number): number => {
  if (subtotal < 10) return 5;
  if (subtotal <= 20) return 8;
  return 10;
};

function cartLinesHaveRequiredText(lines: ChapterSuppliesCartItem[]): boolean {
  return lines.every((line) => {
    if (!line.customization_required) return true;
    return line.custom_text_lines.some((s) => s.trim().length > 0);
  });
}

function cartLinesHaveRequiredPersonalization(
  lines: ChapterSuppliesCartItem[]
): boolean {
  return lines.every((line) => {
    if (!line.personalization_required) return true;
    return Boolean((line.personalization_user_id || '').trim());
  });
}

function linePayloadCustomText(
  line: ChapterSuppliesCartItem
): string[] | undefined {
  if (!line.customization_required) return undefined;
  return line.custom_text_lines.map((s) => s.trim()).filter(Boolean);
}

const StorefrontCheckout: React.FC = () => {
  const navigate = useNavigate();
  const userID = localStorage.getItem('user_id') || '';
  const [cart, setCart] = useState(() => getCart());
  const subtotal = getCartSubtotal();
  const freight = getFreight(subtotal);
  const total = subtotal + freight;
  const needsApproval = cart.some((c) => c.order_approval_required);

  const refreshCart = () => setCart(getCart());

  const { data: profile } = useApiGet(
    ['storefront-checkout-user-profile', userID],
    () => getUserPersonalInfo(userID),
    { enabled: !!userID }
  );

  const primaryAddress = useMemo(
    () =>
      profile?.addresses?.find(
        (a) => a.address_type_id === 'd5107f490a3049d5969ccbb5f843aac7'
      ) || profile?.addresses?.[0],
    [profile]
  );

  const [overrideShipTo, setOverrideShipTo] = useState(false);
  const [shipToName, setShipToName] = useState('');
  const [shipToAddress1, setShipToAddress1] = useState('');
  const [shipToAddress2, setShipToAddress2] = useState('');
  const [shipToCity, setShipToCity] = useState('');
  const [shipToPostalCode, setShipToPostalCode] = useState('');
  const [shipToStateID, setShipToStateID] = useState('');
  const [shipToCountryID, setShipToCountryID] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkoutSession, setCheckoutSession] =
    useState<StorefrontCheckoutResponse | null>(null);

  const selectedCountryID = overrideShipTo
    ? shipToCountryID
    : primaryAddress?.country_id || '';
  const { data: countries = [] } = useApiGet(
    ['storefront-checkout-countries'],
    getCountries
  );
  const { data: states = [] } = useApiGet(
    ['storefront-checkout-states', selectedCountryID],
    () => getStates(selectedCountryID),
    { enabled: !!selectedCountryID }
  );

  const resolvedName =
    overrideShipTo || !profile
      ? shipToName
      : `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  const resolvedAddress1 = overrideShipTo
    ? shipToAddress1
    : primaryAddress?.address_1 || '';
  const resolvedAddress2 = overrideShipTo
    ? shipToAddress2
    : primaryAddress?.address_2 || '';
  const resolvedCity = overrideShipTo ? shipToCity : primaryAddress?.city || '';
  const resolvedPostal = overrideShipTo
    ? shipToPostalCode
    : primaryAddress?.postal_code || '';
  const resolvedStateID = overrideShipTo
    ? shipToStateID
    : primaryAddress?.state_id || '';
  const resolvedCountryID = overrideShipTo
    ? shipToCountryID
    : primaryAddress?.country_id || '';

  const personalizationOk = cartLinesHaveRequiredText(cart);
  const memberSelectionOk = cartLinesHaveRequiredPersonalization(cart);

  const canPlace =
    cart.length > 0 &&
    personalizationOk &&
    memberSelectionOk &&
    !!resolvedName.trim() &&
    !!resolvedAddress1.trim() &&
    !!resolvedCity.trim() &&
    !!resolvedStateID.trim() &&
    !!resolvedPostal.trim() &&
    !!resolvedCountryID.trim() &&
    !!stripePromise;

  const { mutate: submitCheckout, isPending: isSubmitting } = useApiSend(
    checkoutStorefront,
    (resp) => {
      setCheckoutSession(resp);
      setErrorMessage(null);
    },
    (err) => {
      setErrorMessage(getApiErrorMessage(err));
    }
  );

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chapter-supplies/storefront/cart')}
        sx={{ mb: 2 }}
      >
        Back to Cart
      </Button>

      <Typography variant="h4" sx={{ mb: 2 }}>
        Checkout
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Order Summary
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                {cart.map((line) => {
                  const maxLines = Math.max(
                    1,
                    line.max_text_lines ?? (line.custom_text_lines.length || 1)
                  );
                  const maxChars = Math.max(1, line.max_text_length ?? 255);
                  const linesToShow = line.customization_required
                    ? [...line.custom_text_lines]
                    : [];
                  while (
                    line.customization_required &&
                    linesToShow.length < maxLines
                  )
                    linesToShow.push('');

                  return (
                    <Box key={line.line_id}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box sx={{ minWidth: 0, pr: 1 }}>
                          <Typography variant="body2">{line.name}</Typography>
                          {line.variant_label && (
                            <Chip
                              size="small"
                              label={line.variant_label}
                              variant="outlined"
                              sx={{ mt: 0.5 }}
                            />
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Qty {line.quantity} — {formatPrice(line.unit_price)}{' '}
                            each
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ flexShrink: 0 }}>
                          {formatPrice(line.unit_price * line.quantity)}
                        </Typography>
                      </Stack>
                      {line.customization_required &&
                        !line.personalization_required && (
                          <Stack spacing={1} sx={{ mt: 1, pl: 0 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Edit personalization before placing the order.
                            </Typography>
                            {linesToShow.slice(0, maxLines).map((val, idx) => (
                              <TextField
                                key={idx}
                                size="small"
                                fullWidth
                                label={`Line ${idx + 1}`}
                                value={val}
                                onChange={(e) => {
                                  const clipped = clipToMaxChars(
                                    e.target.value,
                                    maxChars
                                  );
                                  setCartLineCustomText(
                                    line.line_id,
                                    idx,
                                    clipped
                                  );
                                  refreshCart();
                                }}
                                helperText={`${storefrontTextCharCount(val)} / ${maxChars}`}
                              />
                            ))}
                          </Stack>
                        )}
                      {line.personalization_required && (
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Select member and confirm the full name for
                            personalization.
                          </Typography>
                          <PersonalizationMemberPicker
                            selectedUserID={
                              line.personalization_user_id || null
                            }
                            selectedDisplayName={
                              line.personalization_display_name || null
                            }
                            fullNameValue={line.personalization_full_name || ''}
                            maxChars={maxChars}
                            onChange={(next) => {
                              setCartLinePersonalization(line.line_id, next);
                              refreshCart();
                            }}
                          />
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
              {!personalizationOk && (
                <Alert severity="warning" variant="filled" sx={{ mb: 1 }}>
                  Each personalized item requires at least one non-empty line.
                </Alert>
              )}
              {!memberSelectionOk && (
                <Alert severity="warning" variant="filled" sx={{ mb: 1 }}>
                  Each member-personalized item requires selecting a member.
                </Alert>
              )}
              {needsApproval && (
                <Alert severity="info" variant="filled">
                  One or more items require approval. In the final flow, payment
                  will be held until approval.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Totals
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography>Subtotal</Typography>
                <Typography>{formatPrice(subtotal)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Freight</Typography>
                <Typography>{formatPrice(freight)}</Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 1 }}
              >
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary.main">
                  {formatPrice(total)}
                </Typography>
              </Stack>
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 2 }}
                disabled={!canPlace || isSubmitting || !!checkoutSession}
                onClick={() =>
                  submitCheckout({
                    ship_to_name: resolvedName.trim(),
                    ship_to_address_line1: resolvedAddress1.trim(),
                    ship_to_address_line2: resolvedAddress2.trim() || null,
                    ship_to_city: resolvedCity.trim(),
                    ship_to_state_id: resolvedStateID.trim(),
                    ship_to_postal_code: resolvedPostal.trim(),
                    ship_to_country_id: resolvedCountryID.trim(),
                    lines: cart.map((line) => ({
                      online_item_id: line.online_item_id,
                      online_item_variant_id:
                        line.online_item_variant_id || null,
                      quantity: line.quantity,
                      custom_text_lines: linePayloadCustomText(line),
                      personalization_user_id: line.personalization_required
                        ? line.personalization_user_id || null
                        : null,
                      personalization_first_name: line.personalization_required
                        ? line.personalization_full_name || null
                        : null,
                    })),
                  })
                }
              >
                {isSubmitting
                  ? 'Preparing payment...'
                  : checkoutSession
                    ? 'Payment session ready'
                    : 'Proceed to Payment'}
              </Button>
              {!stripePromise && (
                <Alert severity="error" variant="filled" sx={{ mt: 1 }}>
                  Stripe publishable key is not configured.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
        {checkoutSession?.payment_client_secret && stripePromise && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Payment
                </Typography>
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: checkoutSession.payment_client_secret,
                  }}
                >
                  <StorefrontPaymentElementForm
                    checkoutSession={checkoutSession}
                    onError={setErrorMessage}
                    onSuccess={(confirmed) =>
                      navigate('/chapter-supplies/storefront/confirmation', {
                        state: { ...checkoutSession, ...confirmed },
                      })
                    }
                  />
                </Elements>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Ship To
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={overrideShipTo}
                    onChange={(e) => setOverrideShipTo(e.target.checked)}
                  />
                }
                label="Override default shipping address"
              />
              <Stack spacing={2} sx={{ mt: 1 }}>
                {errorMessage && (
                  <Alert severity="error" variant="filled">
                    {errorMessage}
                  </Alert>
                )}
                <TextField
                  label="Name"
                  value={overrideShipTo ? shipToName : resolvedName}
                  onChange={(e) => setShipToName(e.target.value)}
                  disabled={!overrideShipTo}
                  fullWidth
                  size="small"
                  required
                />
                <TextField
                  label="Address 1"
                  value={overrideShipTo ? shipToAddress1 : resolvedAddress1}
                  onChange={(e) => setShipToAddress1(e.target.value)}
                  disabled={!overrideShipTo}
                  fullWidth
                  size="small"
                  required
                />
                <TextField
                  label="Address 2"
                  value={overrideShipTo ? shipToAddress2 : resolvedAddress2}
                  onChange={(e) => setShipToAddress2(e.target.value)}
                  disabled={!overrideShipTo}
                  fullWidth
                  size="small"
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="City"
                      value={overrideShipTo ? shipToCity : resolvedCity}
                      onChange={(e) => setShipToCity(e.target.value)}
                      disabled={!overrideShipTo}
                      fullWidth
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!overrideShipTo}
                    >
                      <InputLabel>Country</InputLabel>
                      <Select
                        value={
                          overrideShipTo ? shipToCountryID : resolvedCountryID
                        }
                        label="Country"
                        onChange={(e) => {
                          const nextID = String(e.target.value || '');
                          setShipToCountryID(nextID);
                          setShipToStateID('');
                        }}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.id} value={country.id}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={!overrideShipTo || !selectedCountryID}
                    >
                      <InputLabel>State/Province</InputLabel>
                      <Select
                        value={overrideShipTo ? shipToStateID : resolvedStateID}
                        label="State/Province"
                        onChange={(e) => {
                          const nextID = String(e.target.value || '');
                          setShipToStateID(nextID);
                        }}
                      >
                        {states.map((state) => (
                          <MenuItem key={state.id} value={state.id}>
                            {state.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Postal Code"
                      value={overrideShipTo ? shipToPostalCode : resolvedPostal}
                      onChange={(e) => setShipToPostalCode(e.target.value)}
                      disabled={!overrideShipTo}
                      fullWidth
                      size="small"
                      required
                    />
                  </Grid>
                </Grid>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

type StorefrontPaymentElementFormProps = {
  checkoutSession: StorefrontCheckoutResponse;
  onError: (msg: string | null) => void;
  onSuccess: (confirmed: {
    order_status_id: string;
    order_status_name: string;
    stripe_payment_fee?: number;
  }) => void;
};

const StorefrontPaymentElementForm: React.FC<
  StorefrontPaymentElementFormProps
> = ({ checkoutSession, onError, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePay = async (event: React.FormEvent) => {
    event.preventDefault();
    onError(null);
    if (!stripe || !elements) return;
    setIsSubmitting(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (error) {
        onError(
          error.message ||
            'Unable to complete payment. Please check card details and try again.'
        );
        return;
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        onError(
          'Payment is still processing. Please wait and refresh your orders list.'
        );
        return;
      }
      const confirmed = await confirmStorefrontPayment({
        order_id: checkoutSession.order_id,
        payment_intent_id:
          paymentIntent.id || checkoutSession.payment_intent_id || '',
      });
      onSuccess({
        order_status_id: confirmed.order_status_id,
        order_status_name: confirmed.order_status_name,
        stripe_payment_fee: confirmed.stripe_payment_fee,
      });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Unable to finalize payment.';
      onError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handlePay}>
      <PaymentElement />
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || !elements || isSubmitting}
        >
          {isSubmitting ? 'Processing...' : 'Pay now'}
        </Button>
      </Stack>
    </Box>
  );
};

export default StorefrontCheckout;
